import * as React from "react";
import HomePageFilterComponent from "../../../components/HomePageFilterComponent";
import BaseFilter from "../../../core/models/BaseFilter";
import { useHubx } from "../../../HubX";
import {
  EVENT_HOMEPAGE_CHANGE_FILTER,
  EVENT_HOMEPAGE_OPEN_ALL,
  EVENT_HOMEPAGE_CLOSE_ALL,
  EVENT_HOME_PAGE_PRODUCT_FILTER_RECEIVE,
  EVENT_HOME_PAGE_PRODUCT_FAVORITE_FILTER_RECEIVE,
  EVENT_HOME_PAGE_PRODUCT_FILTER_CHANGE,
  EVENT_HOME_PAGE_STATUS_FILTER_CHANGE,
  EVENT_HOME_PAGE_SMART_FILTER_CHANGE,
  EVENT_HOME_PAGE_TABLE_COLUMNS_SELECTION_CHANGE,
  OPEN_EXPAND_DIALOG,
  ALL_EXPANDED,
  EVENT_HOME_PAGE_TRIP_FILTER_CHANGE,
  EVENT_HOME_PAGE_UF_FILTER_CHANGE,
} from "../../../Events";
import { HomePageViewType } from "..";
import { useHubXValue } from "../../../core/hooks/useHubHooks";
import { INSTALLATION_LEVEL_STATUS_TYPES } from "../../../core/models/LevelStatus";
import { getLevelColor, getLevelStatusString } from "../../../helpers/installationTankHelpers";
import { HomeTableColumns, HomeTableColumnsType } from "../../../components/HomeTable";
import includes from "lodash/includes";
import { toggleElementAll } from "../../../helpers/utility";
import { useSnackbar } from "notistack";
import { capitalize, sortBy, uniq } from "lodash";
import { FETCH_ALL_INSTALLATIONS_FILTERS } from "../../../Fedx";
import useConstructor from "../../../core/hooks/useConstructor";
import { AllInstallationsFilterGet } from "../../../core/models/AllInstallationsFilter";
import { TRIP_STATUS_TYPES } from "../../../core/models/TripStatus";

function HomePageFilter() {
  const hub = useHubx();
  const toastr = useSnackbar();
  const [view, setView] = React.useState<HomePageViewType>("card");
  const [product, setProduct] = React.useState<string[]>(["all"]);
  const [status, setStatus] = React.useState<string[]>(["all"]);
  const [selectedUF, setSelectedUF] = React.useState<string[]>(["all"]);
  const [selectedTripStatus, setSelectedTripStatus] = React.useState<string[] | "all">("all");
  const [textField, setTextField] = React.useState<string>("");
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = React.useState<HomeTableColumnsType[]>([
    ...HomeTableColumns,
  ]);

  const allExpanded = useHubXValue<boolean>(ALL_EXPANDED, false);

  const [mobileDialogFiltersOpen, setMobileDialogFiltersOpen] = React.useState<boolean>(false);

  const productsFromCustomers = useHubXValue<string[]>(EVENT_HOME_PAGE_PRODUCT_FILTER_RECEIVE, []);

  const productsFromFavorites = useHubXValue<string[]>(EVENT_HOME_PAGE_PRODUCT_FAVORITE_FILTER_RECEIVE, []);

  const [filtersOptions, setFiltersOptions] = React.useState<null | AllInstallationsFilterGet>(
    null
  );

  const handleChangeProduct = React.useCallback(
    (e: React.ChangeEvent<{ value: string[] }>) => {
      const newValue = toggleElementAll(e.target.value);
      setProduct(newValue);
      hub.notify(EVENT_HOME_PAGE_PRODUCT_FILTER_CHANGE, newValue !== ["all"] ? newValue : "");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allExpanded]
  );

  const fetchFilters = async () => {
    try {
      const filters = await hub.request(FETCH_ALL_INSTALLATIONS_FILTERS);
      setFiltersOptions(filters);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("🚀 ~ file: index.tsx ~ line 43 ~ fetchFilters ~ error", error);
      toastr.enqueueSnackbar("Ops! Ocorreu um erro ao obter as opções dos filtros!");
    }
  };

  useConstructor(async () => {
    await fetchFilters();
  });

  const handleChangeStatus = React.useCallback(
    (e: React.ChangeEvent<{ value: string[] }>) => {
      const newValue = toggleElementAll(e.target.value);
      setStatus(newValue);
      hub.notify(EVENT_HOME_PAGE_STATUS_FILTER_CHANGE, newValue !== ["all"] ? newValue : "");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allExpanded]
  );

  const handleChangeTripStatus = React.useCallback(
    (e: React.ChangeEvent<{ value: string[] }>) => {
      const newValue = toggleElementAll(e.target.value);
      setSelectedTripStatus(newValue);
      hub.notify(EVENT_HOME_PAGE_TRIP_FILTER_CHANGE, newValue != ["all"] ? newValue : "");
    },
    [allExpanded]
  );

  const handleChangeUF = React.useCallback(
    (e: React.ChangeEvent<{ value: string | string[] }>) => {
      const aux = e.target.value as string[];
      const newValue = toggleElementAll(aux);
      setSelectedUF(newValue);
      hub.notify(EVENT_HOME_PAGE_UF_FILTER_CHANGE, newValue !== ["all"] ? newValue : "");
    },
    [allExpanded]
  );

  const filters: BaseFilter[] = React.useMemo(
    () => [
      {
        id: "product",
        items: [
          { key: "all", value: <strong>Todos</strong> },
          ...sortBy(
            uniq(productsFromCustomers.concat(productsFromFavorites)).map((x) => {
              return { key: x, value: x };
            }),
            "key"
          ),
        ],
        label: "Produto",
        multiple: true,
        value: product,
        handleChange: handleChangeProduct,
      },
      {
        id: "status",
        items: [
          { key: "all", value: <strong>Todos</strong> },
          ...INSTALLATION_LEVEL_STATUS_TYPES.map((x) => {
            return {
              key: x,
              value: (
                <span>
                  <span style={{ color: `${getLevelColor(x)}` }}>●</span> {getLevelStatusString(x)}
                </span>
              ),
            };
          }),
        ],
        label: "Status",
        multiple: true,
        value: status,
        handleChange: handleChangeStatus,
      },
      {
        id: "uf",
        items: [
          { key: "all", value: <strong>Todos</strong> },
          ...sortBy(filtersOptions?.installation_uf ?? [])?.map((x: string) => {
            return { key: x, value: x };
          }),
        ],
        label: "Estado",
        multiple: true,
        value: selectedUF,
        handleChange: handleChangeUF,
      },
      {
        id: "trip_status",
        items: [
          { key: "all", value: <strong>Todos</strong> },
          ...TRIP_STATUS_TYPES?.map((x: string) => {
            return { key: x, value: capitalize(x) };
          }),
        ],
        label: "Viagem",
        multiple: true,
        value: selectedTripStatus,
        handleChange: handleChangeTripStatus,
      },
    ],
    [
      handleChangeProduct,
      handleChangeStatus,
      handleChangeTripStatus,
      handleChangeUF,
      product,
      productsFromCustomers,
      status,
      selectedTripStatus,
      selectedUF,
    ]
  );

  const toggleView = () => {
    const nextState = view === "card" ? "table" : "card";
    hub.notify(EVENT_HOMEPAGE_CHANGE_FILTER, nextState);
    setView(nextState);
  };

  const handleClickExpandAll = () => {
    hub.notify(EVENT_HOMEPAGE_OPEN_ALL);
  };

  const handleClickCloseAll = () => {
    hub.notify(EVENT_HOMEPAGE_CLOSE_ALL);
  };

  const handleChangeTextField = (e: React.ChangeEvent<{ value: string }>) => {
    const newValue = e.target.value;
    setTextField(newValue);
    hub.notify(EVENT_HOME_PAGE_SMART_FILTER_CHANGE, newValue);
  };

  const handleClickColumnOption = (column: HomeTableColumnsType) => {
    let newValue;
    if (includes(selectedColumns, column)) {
      newValue = selectedColumns.filter((x) => x !== column);
      setSelectedColumns(newValue);
    } else {
      newValue = [...selectedColumns, column];
      setSelectedColumns((prev) => [...prev, column]);
    }

    hub.notify(EVENT_HOME_PAGE_TABLE_COLUMNS_SELECTION_CHANGE, newValue);
  };

  return (
    <HomePageFilterComponent
      handleToggleView={toggleView}
      handleChangeSearch={handleChangeTextField}
      handleClickExpandAll={handleClickExpandAll}
      handleClickCloseAll={handleClickCloseAll}
      textFieldValue={textField}
      filters={filters}
      selectedView={view}
      tableDialogOpen={dialogOpen}
      handleCloseDialog={() => setDialogOpen(false)}
      handleOpenDialog={() => setDialogOpen(true)}
      columnsSelected={selectedColumns}
      onClickColumnOption={handleClickColumnOption}
      mobileDialogOpen={mobileDialogFiltersOpen}
      handleOpenMobileDialog={() => setMobileDialogFiltersOpen(true)}
      handleCloseMobileDialog={() => {
        setMobileDialogFiltersOpen(false);
      }}
    />
  );
}

export default HomePageFilter;
